<!-- Play. The one screen that is not a DOM page: a full-bleed canvas with a
     HUD overlay. No AppHeader by design (see DESIGN.md). The round runs on a
     requestAnimationFrame loop stepping a deterministic sim; every visual
     effect comes from the sim's events. -->
<template>
  <IonPage>
    <IonContent :scroll-y="false" class="play-content">
      <div class="play-stage">
        <div ref="columnEl" class="play-column">
          <canvas
            ref="canvasEl"
            class="play-canvas"
            role="img"
            :aria-label="canvasLabel"
            @pointerdown="onPointer"
          />

          <Hud
            v-if="phase === 'play' || phase === 'paused'"
            :time-left-ms="hud.timeLeftMs"
            :duration-ms="hud.durationMs"
            :score="hud.score"
            :multiplier="hud.multiplier"
            :meter="hud.meter"
            :show-meter="hasStars"
            :target="run?.target ?? 0"
            :show-release="phase === 'play' && hud.parked"
            :show-burst="phase === 'play' && hud.canBurst"
            @release="doRelease"
            @burst="doBurst"
            @pause="togglePause"
          />

          <!-- The reveal. The HUD is gone by design: the last thing on screen
               is the street, lit, and one plain sentence about it. -->
          <div v-if="phase === 'reveal' && revealCard" class="reveal" role="status">
            <p class="reveal__street">{{ run?.level?.name }} is on</p>
            <p class="reveal__count tnum">{{ revealCard.homesLit }} of {{ revealCard.homesTotal }} homes</p>
          </div>

          <MomentCaption v-if="caption" :key="caption.key" :caption="caption.text" />

          <JunctionButtons
            v-if="phase === 'play'"
            :can-release="hud.parked"
            :can-burst="hud.canBurst"
            @switch="switchNextJunction"
            @release="doRelease"
            @burst="doBurst"
          />

          <!-- Pre-round card: the street's name, and any mechanic that is new. -->
          <div v-if="phase === 'pre'" class="play-overlay" @click="beginRound">
            <div class="play-card" role="dialog" aria-labelledby="pre-title">
              <p class="soc-muted">{{ modeLine }}</p>
              <h2 id="pre-title">{{ run?.level?.name }}</h2>
              <p v-if="!newMechanics.length">{{ run?.level?.homes?.length }} homes to switch on in 45 seconds.</p>
              <MechanicPicture v-for="m in newMechanics" :key="m" :id="m" />
              <div class="play-card__actions">
                <IonButton class="soc-play" @click.stop="beginRound">{{ newMechanics.length ? 'Got it' : 'Go' }}</IonButton>
              </div>
            </div>
          </div>

          <div v-if="phase === 'paused'" class="play-overlay">
            <div class="play-card" role="dialog" aria-labelledby="pause-title">
              <h2 id="pause-title">Paused</h2>
              <p>The spark is waiting.</p>
              <div class="play-card__actions">
                <IonButton class="soc-play" @click="togglePause">Resume</IonButton>
                <IonButton class="soc-ghost" @click="quit">Back to street</IonButton>
              </div>
            </div>
          </div>

          <div v-if="phase === 'locked'" class="play-overlay">
            <div class="play-card" role="dialog" aria-labelledby="locked-title">
              <h2 id="locked-title">Door {{ lockedDay }} opens on {{ lockedDay }} December</h2>
              <p>Every door is the same street for everyone, so it waits for the day. Free Play is open now.</p>
              <div class="play-card__actions">
                <IonButton class="soc-play" @click="freePlayInstead">Play Free Play</IonButton>
                <IonButton class="soc-ghost" @click="quit">Back to street</IonButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
/* global performance */
import { ref, reactive, computed, onBeforeUnmount, watch } from 'vue';
import { IonPage, IonContent, IonButton, onIonViewDidEnter, onIonViewWillLeave, useIonRouter } from '@ionic/vue';
import { useRoute, useRouter } from 'vue-router';
import { app } from '../core/app.js';
import { announce } from '../core/a11y.js';
import { tap, boop, tada } from '../core/audio.js';
import { haptic, toast } from '../core/util.js';
import { settings, progress, setLastResult, persistProgress, reducedMotion } from '../store.js';
import { generateLevel, validateLevel, MOMENTS, MECHANICS } from '../lib/level-gen.js';
import { createSim } from '../lib/sim.js';
import { seedForDay, isDayUnlocked, mechanicsForDay, tierForDay, freePlayMechanics } from '../lib/daily.js';
import { decodeChallenge } from '../lib/challenge.js';
import { createRenderer } from '../lib/renderer.js';
import { recordResult, maxSeenDay } from '../lib/progress.js';
import * as music from '../lib/music.js';
import Hud from '../components/Hud.vue';
import JunctionButtons from '../components/JunctionButtons.vue';
import MomentCaption from '../components/MomentCaption.vue';
import MechanicPicture from '../components/MechanicPicture.vue';

const route = useRoute();
const router = useRouter();
const ionRouter = useIonRouter();

const canvasEl = ref(null);
const columnEl = ref(null);

// 'idle' | 'pre' | 'play' | 'paused' | 'reveal' | 'locked'
const phase = ref('idle');
const run = ref(null);
const lockedDay = ref(0);
const caption = ref(null);
const newMechanics = ref([]);
const hud = reactive({ timeLeftMs: 45000, durationMs: 45000, score: 0, multiplier: 1, meter: 0, parked: false, canBurst: false });
const revealCard = ref(null);

// The Good Energy Meter only fills from Solar Stars. On a level without them
// it is a control that can never move, so it is not shown at all.
const hasStars = computed(() => !!run.value?.level?.edges?.some((e) => e.star));

const modeLine = computed(() => {
  const r = run.value;
  if (!r) return '';
  if (r.mode === 'day') return `Today's Street · Door ${r.day}`;
  if (r.mode === 'challenge') return `A friend's challenge · beat ${r.target} ${r.target === 1 ? 'home' : 'homes'}`;
  return 'Free Play';
});
const canvasLabel = computed(() => {
  const r = run.value;
  return r ? `${r.level.name}: a street of ${r.level.homes.length} homes at dusk. Tap a junction to switch the spark.` : 'The street at dusk';
});

let renderer = null;
let sim = null;
let raf = 0;
let lastFrame = 0;
let revealStart = 0;
let preTimer = 0;
let captionTimer = 0;
let captionKey = 0;
let bulbCounter = 0;
let ro = null;
let finished = false;
// Rebuilt once per round, not once per frame: junctionsAhead() runs 60 times a
// second and was allocating two Maps over the whole level on every call.
let edgeIndex = new Map();
let nodeIndex = new Map();
const MOMENT_BY_ID = new Map(MOMENTS.map((m) => [m.id, m]));

// --- resolving the round --------------------------------------------------

// The day whose mechanic set Free Play starts from: Battery Bauble and Solar
// Star. Today's Street still unlocks them in order, one door at a time.
const FREE_PLAY_FLOOR_DAY = 3;

function randomSeed() {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0];
}

function resolveRun(mode, code) {
  if (mode === 'day') {
    const day = Number(code);
    if (!Number.isInteger(day) || day < 1 || day > 24) return { error: true };
    if (!isDayUnlocked(day)) return { locked: day };
    const seed = seedForDay(day);
    const level = generateLevel(seed, { tier: tierForDay(day), mechanics: mechanicsForDay(day) });
    return { mode, seed, day, level, practice: settings.practiceSpeed };
  }
  if (mode === 'challenge') {
    const c = decodeChallenge(String(code ?? ''));
    if (!c) return { error: true };
    const level = generateLevel(c.seed, { tier: 2, mechanics: MECHANICS });
    return { mode, seed: c.seed, code, target: c.homesLit, targetScore: c.score, level, practice: settings.practiceSpeed };
  }
  const seed = randomSeed();
  const tier = tierForDay(Math.max(1, maxSeenDay(progress)));
  // Free Play never starts plain. `freePlayMechanics([])` is [] by design — the
  // advent calendar introduces one mechanic at a time — but Free Play is also
  // the first thing anyone taps on a clean install, and a round with no bauble
  // and no star is the least interesting version of this game. Floor it at the
  // day-3 set so the first round always has something to discover, and let real
  // progress take over from there.
  const seenFloor = Math.max(FREE_PLAY_FLOOR_DAY, maxSeenDay(progress));
  const mechanics = freePlayMechanics([...(progress.seenDays ?? []), seenFloor]);
  const level = generateLevel(seed, { tier, mechanics });
  return { mode: 'free', seed, level, practice: settings.practiceSpeed };
}

function setup() {
  teardownRound();
  const mode = String(route.params.mode ?? 'free');
  const code = route.params.code;
  const r = resolveRun(mode, code);
  if (r.error) {
    toast('That link didn’t work');
    leave();
    return;
  }
  if (r.locked) {
    lockedDay.value = r.locked;
    run.value = null;
    phase.value = 'locked';
    return;
  }
  const check = validateLevel(r.level);
  if (check && check.ok === false) {
    console.error('level invalid', check.errors);
    toast('That street couldn’t be built');
    leave();
    return;
  }
  run.value = r;
  edgeIndex = new Map(r.level.edges.map((e) => [e.id, e]));
  nodeIndex = new Map(r.level.nodes.map((n) => [n.id, n]));
  sim = createSim(r.level, { practice: !!r.practice });
  finished = false;
  bulbCounter = 0;
  hud.durationMs = r.level.durationMs;
  hud.timeLeftMs = r.level.durationMs;
  hud.score = 0; hud.multiplier = 1; hud.meter = 0; hud.parked = false; hud.canBurst = false;

  ensureRenderer();
  renderer.setLevel(r.level);
  newMechanics.value = r.level.mechanics.filter((m) => !progress.seenMechanics.includes(m));

  // OTA reloads wait while a round is on.
  app.isIdle = () => false;

  phase.value = 'pre';
  clearTimeout(preTimer);
  // Long enough to read what is new, and a deep link into a late door can
  // introduce all six mechanics at once.
  const readMs = newMechanics.value.length ? Math.min(16000, 3500 + newMechanics.value.length * 2600) : 1500;
  preTimer = setTimeout(beginRound, readMs);
  if (!raf) raf = requestAnimationFrame(frame);
}

function ensureRenderer() {
  if (renderer) return;
  renderer = createRenderer(canvasEl.value, { highContrast: settings.highContrast, reducedMotion: reducedMotion() });
  ro = new ResizeObserver(() => renderer?.resize());
  ro.observe(columnEl.value);
}

function beginRound() {
  if (phase.value !== 'pre') return;
  clearTimeout(preTimer);
  for (const m of newMechanics.value) if (!progress.seenMechanics.includes(m)) progress.seenMechanics.push(m);
  if (newMechanics.value.length) persistProgress();
  newMechanics.value = [];
  phase.value = 'play';
  lastFrame = 0;
  if (settings.music) music.start();
  music.setEnabled(settings.music);
  announce(`${run.value.level.name}. The spark is off.`);
}

// --- the loop -----------------------------------------------------------

function frame(now) {
  raf = requestAnimationFrame(frame);
  if (!renderer || !sim || !run.value) return;
  const p = phase.value;
  if (p === 'play') {
    const dt = lastFrame ? Math.min(50, now - lastFrame) : 16;
    lastFrame = now;
    sim.step(dt);
    handleEvents(sim.consumeEvents());
    chargeBaubles(dt);
    syncHud();
    renderer.draw(run.value.level, sim.state, { now, approaching: junctionsAhead() });
  } else if (p === 'reveal') {
    // Three seconds of pull-back, the sentence at the half-way point, then a
    // beat to read it. Reduced motion cuts straight to the finished street.
    const dur = reducedMotion() ? 1 : REVEAL_MS;
    const elapsed = now - revealStart;
    const k = Math.min(1, elapsed / dur);
    renderer.draw(run.value.level, sim.state, { now, approaching: [], reveal: k });
    if (!revealCard.value && elapsed > dur * 0.5) revealCard.value = sim.result();
    if (elapsed > dur + REVEAL_HOLD_MS) toResults();
  } else {
    // pre / paused: keep the picture alive, nothing advances.
    lastFrame = 0;
    renderer.draw(run.value.level, sim.state, { now, approaching: junctionsAhead() });
  }
}

// A spark parked in a Battery Bauble waits for Release. Left alone it waits
// FOREVER — a player who does not know the mechanic yet loses the whole round
// standing still, which is the one failure state this app is not allowed to
// have. So the bauble charges over BAUBLE_HOLD_MS and lets go by itself.
// Releasing early is still the skilful move: you choose the moment.
const BAUBLE_HOLD_MS = 3600;

function syncHud() {
  const s = sim.state;
  hud.timeLeftMs = s.timeLeftMs;
  hud.score = s.score;
  hud.multiplier = s.effectiveMultiplier ?? s.multiplier;
  hud.meter = s.meter;
  hud.parked = s.sparks.some((sp) => sp.parked);
  hud.canBurst = s.meter >= 1;
}

function chargeBaubles(dtMs) {
  let full = false;
  for (const spark of sim.state.sparks) {
    if (!spark.parked) { spark.charge = 0; continue; }
    spark.charge = Math.min(1, (spark.charge ?? 0) + dtMs / BAUBLE_HOLD_MS);
    if (spark.charge >= 1) full = true;
  }
  if (full) doRelease();
}

function handleEvents(events) {
  if (!events.length) return;
  renderer.pushEvents(events, sim.state);
  for (const ev of events) {
    switch (ev.type) {
      case 'bulb':
        bulbCounter += 1;
        if (bulbCounter % 4 === 0 && settings.sound) boop();
        break;
      case 'home': {
        if (settings.sound) tada();
        haptic('pop');
        if (settings.music) music.addLayer();
        const home = run.value.level.homes.find((h) => h.id === ev.homeId);
        const m = MOMENT_BY_ID.get(ev.moment);
        showCaption(m?.caption ?? `${home?.name ?? 'A home'} switched on.`);
        announce(`${home?.name ?? 'A home'} switched on. ${m?.caption ?? ''} Multiplier ${ev.multiplier}.`);
        break;
      }
      case 'star':
        if (settings.sound) boop();
        haptic('tick');
        break;
      case 'burst':
        if (settings.sound) tada();
        haptic('heavy');
        announce(`Burst. ${ev.bulbs} bulbs lit.`);
        break;
      case 'bell':
        if (settings.sound) tada();
        break;
      case 'bauble-enter':
        haptic('tick');
        announce('The spark is charging in the bauble. Release when ready.');
        break;
      case 'split':
        haptic('pop');
        announce('Double spark.');
        break;
      case 'finish':
        finishRound();
        break;
      default:
        break;
    }
  }
}

// Long enough to read a sentence while the round carries on. The CSS fade
// (`caption-in`) is written to match.
const CAPTION_MS = 2300;

function showCaption(text) {
  captionKey += 1;
  caption.value = { text, key: captionKey };
  clearTimeout(captionTimer);
  captionTimer = setTimeout(() => { caption.value = null; }, CAPTION_MS);
}

const REVEAL_MS = 3000;
const REVEAL_HOLD_MS = 1600;

function finishRound() {
  if (finished) return;
  finished = true;
  music.stop();
  clearTimeout(captionTimer);
  caption.value = null;
  revealCard.value = null;
  phase.value = 'reveal';
  revealStart = performance.now();
  const res = sim.result();
  announce(`Time. ${res.homesLit} of ${res.homesTotal} homes lit.`);
}

function toResults() {
  if (!sim || !run.value) return;
  const r = run.value;
  const res = sim.result();
  const outcome = recordResult(progress, res, r);
  persistProgress();
  const result = {
    ...res,
    id: `${r.mode}-${r.seed}-${Date.now()}`,
    mode: r.mode,
    day: r.day ?? null,
    code: r.code ?? null,
    target: r.target ?? null,
    targetScore: r.targetScore ?? null,
    practice: !!r.practice,
    newBest: outcome.newBest,
    unlocked: outcome.unlocked,
    playedAt: Date.now(),
    nationalRecorded: false,
  };
  setLastResult(result, { mode: r.mode, seed: r.seed, day: r.day, code: r.code, level: r.level, practice: r.practice, target: r.target });
  stopLoop();
  router.replace({ name: 'results' });
}

// --- input --------------------------------------------------------------

function onPointer(e) {
  if (phase.value === 'pre') { beginRound(); return; }
  if (phase.value !== 'play' || !sim || !renderer) return;
  const rect = canvasEl.value.getBoundingClientRect();
  const w = renderer.toWorld(e.clientX - rect.left, e.clientY - rect.top);
  const id = sim.hitTest(w.x, w.y, 56);
  if (!id) return;
  const node = run.value.level.nodes.find((n) => n.id === id);
  if (node?.kind === 'bauble') { doRelease(); return; }
  if (sim.tapJunction(id)) {
    if (settings.sound) tap();
    haptic('tick');
  }
}

/**
 * The next junction each spark will actually reach, following the switches
 * that are set right now.
 *
 * `sim.approaching()` answers a different question — the very next NODE — and
 * most nodes are bends. Highlighting only that gave the player no lead time at
 * all: the target lit up on the last cable before the turn, which on a short
 * cable is under a second. Walking the chain forward means the decision is
 * visible and pre-settable from a few cables away, which is what makes this a
 * one-thumb game rather than a reflex test.
 */
const LEAD_HOPS = 4;
function junctionsAhead() {
  const level = run.value?.level;
  if (!level || !sim) return [];
  const out = [];
  const seen = new Set();
  sim.state.sparks.forEach((spark, sparkIndex) => {
    let edge = edgeIndex.get(spark.edgeId);
    if (!edge) return;
    let distance = (1 - spark.t) * edge.length;
    for (let hops = 0; hops < LEAD_HOPS && edge; hops++) {
      const node = nodeIndex.get(edge.to);
      if (!node) return;
      if (node.kind === 'junction' || node.kind === 'split') {
        if (!seen.has(node.id)) {
          seen.add(node.id);
          out.push({ sparkIndex, nodeId: node.id, distance });
        }
        return;
      }
      edge = edgeIndex.get(node.out[sim.switchIndex(node.id)] ?? node.out[0]);
      if (edge) distance += edge.length;
    }
  });
  return out;
}

function nextJunctionAhead() {
  return junctionsAhead()[0]?.nodeId ?? null;
}

function switchNextJunction() {
  if (phase.value !== 'play' || !sim) return;
  const id = nextJunctionAhead();
  if (id && sim.tapJunction(id)) {
    if (settings.sound) tap();
    haptic('tick');
    announce('Junction switched.');
  }
}

function doRelease() {
  if (phase.value !== 'play' || !sim) return;
  if (sim.release()) { if (settings.sound) tap(); haptic('pop'); }
}

function doBurst() {
  if (phase.value !== 'play' || !sim) return;
  sim.burst();
}

function togglePause() {
  if (phase.value === 'play') { phase.value = 'paused'; music.setEnabled(false); }
  else if (phase.value === 'paused') { phase.value = 'play'; lastFrame = 0; music.setEnabled(settings.music); }
}

function onKey(e) {
  if (e.defaultPrevented || e.target?.tagName === 'INPUT') return;
  const p = phase.value;
  if (p === 'pre' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); beginRound(); return; }
  if (p !== 'play' && p !== 'paused') return;
  switch (e.key) {
    case 'ArrowLeft': case 'ArrowRight': case 'a': case 'd': case 'A': case 'D':
      e.preventDefault(); switchNextJunction(); break;
    case ' ':
      e.preventDefault();
      if (hud.parked) doRelease(); else if (hud.canBurst) doBurst();
      break;
    case 'Escape':
      e.preventDefault(); togglePause(); break;
    default:
      break;
  }
}

function onVisibility() {
  if (document.hidden && phase.value === 'play') togglePause();
}

// --- navigation ---------------------------------------------------------

function leave() {
  stopLoop();
  if (ionRouter.canGoBack()) ionRouter.back(); else router.replace({ name: 'title' });
}
function quit() {
  music.stop();
  leave();
}
function freePlayInstead() {
  router.replace({ name: 'play', params: { mode: 'free' } });
  setup();
}

function stopLoop() {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

function teardownRound() {
  stopLoop();
  clearTimeout(preTimer);
  clearTimeout(captionTimer);
  caption.value = null;
  revealCard.value = null;
  music.stop();
  sim = null;
  delete app.isIdle;
}

watch(() => [settings.highContrast, settings.reducedMotion], () => renderer?.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() }));
watch(() => settings.music, (on) => music.setEnabled(on));

// A window onto the round, for `npm run dev` only. QA drives a real round by
// reading `sim.approaching()` and mapping junctions through `renderer.toScreen`,
// which is impossible from outside otherwise. Vite drops the whole block from a
// production build.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__soc = {
    sim: () => sim,
    renderer: () => renderer,
    phase: () => phase.value,
    run: () => run.value,
    canvas: () => canvasEl.value,
  };
}

onIonViewDidEnter(() => {
  window.addEventListener('keydown', onKey);
  document.addEventListener('visibilitychange', onVisibility);
  setup();
});
onIonViewWillLeave(() => {
  window.removeEventListener('keydown', onKey);
  document.removeEventListener('visibilitychange', onVisibility);
  teardownRound();
  phase.value = 'idle';
});
onBeforeUnmount(() => {
  teardownRound();
  ro?.disconnect();
  renderer?.destroy();
  renderer = null;
});
</script>

<style scoped>
.play-content {
  --background: var(--bg);
  --padding-top: 0;
}
</style>
